import React, {Fragment} from 'react';
import {
  useAccountQueryActionContext,
  useAccountActions,
} from '../../hooks/api/actions';
import {
  AccountAction,
  AccountActionQueryLimit,
  MetadataResponse,
} from '../../shared/api/api';
import {
  Box,
  Button,
  ButtonProps,
  Stack,
  Tooltip,
  TooltipProps,
  forwardRef,
  useDisclosure,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';
import {useIsMyAccountsView} from './AccountViewContext';
import {useGridStore} from './gridStore';
import {useIsCrmSyncing} from '../../hooks/api/crm';
import {
  useCustomer,
  useIsFreeOrListBuilderCustomer,
  useIsFreemiumCustomer,
} from '../../hooks/api/metadata';
import {
  TaskRemove,
  Redo,
  CheckmarkOutline,
  Close,
  ArrowsVertical,
  TagEdit,
  TagNone,
} from '@carbon/icons-react';
import _ from 'lodash';
import {useActions, TierOverridePopout} from './AccountGrid.actions';
import {AccountTagsEditorModal} from '../AccountTagsEditor';
import {ScoredAccount} from '../../shared/scoredAccounts';
import {useIsRescoring} from '../../hooks/api/scoringInfo';
import {getLimit, getSavesQuota} from '../../shared/customer';
import {useScoringRun} from '../../context/ScoringRunContext';
import {useShouldAutoSelectRows} from './useShouldAutoSelectRows';

export const ActionButtons = ({
  fetchedAccounts,
  numSelected,
}: {
  fetchedAccounts: ScoredAccount[];
  numSelected: number;
}) => {
  const toast = useToast();
  const isMyAccountsView = useIsMyAccountsView();
  const tagAccountsModal = useDisclosure();
  const removeTagsModal = useDisclosure();

  const customer = useCustomer();
  const isFreemiumCustomer = useIsFreemiumCustomer();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  const isCrmSyncing = useIsCrmSyncing();
  const {resetRowSelection, rowSelection, numBulkSelected} = useGridStore();
  const isRescoring = useIsRescoring();
  const scoringRun = useScoringRun();
  const actionContext = useAccountQueryActionContext();
  const accountActions = useAccountActions(actionContext);
  const isCompact = useBreakpointValue({base: true, xl: false});

  const updateAccounts = ({action}: {action: AccountAction}) => {
    if (!actionContext) {
      return;
    }

    accountActions.mutate(
      {action},
      {
        onSuccess: () => {
          if (action.name === 'override') {
            const descriptionPieces = [
              action.override === 'none' && 'Removed overrides from ',
              `${numSelected} account${numSelected !== 1 ? 's' : ''}`,
              action.override !== 'none' && ` moved to Tier ${action.override}`,
              '.',
            ].filter((x) => x);

            toast({
              title: 'Override Tier successful',
              description: descriptionPieces.join(''),
              status: 'success',
              duration: 7_000,
              isClosable: true,
              position: 'bottom-left',
            });
          }

          resetRowSelection();
        },
      }
    );
  };

  const handleSaveAndTag = () => {
    if (!isMyAccountsView && isFreeOrListBuilderCustomer) {
      accountActions.mutate(
        {action: {name: 'tag'}},
        {
          onSuccess: () => {
            resetRowSelection();
          },
        }
      );
    } else {
      tagAccountsModal.onOpen();
    }
  };

  const disabledActionTooltip = _.compact([
    // List of reasons why actions might be disabled. The first one "wins"
    // when determining what message to show to the user.
    isCrmSyncing ? 'Actions are disabled during CRM sync' : null,
    isRescoring ? 'Actions are disabled during data refresh' : null,
    numSelected > AccountActionQueryLimit
      ? `Actions cannot be performed on more than ${AccountActionQueryLimit} accounts`
      : null,
  ]).at(0);

  const isFreemiumDiscover = isFreemiumCustomer && !isMyAccountsView;

  const autoSelectTopRows = useShouldAutoSelectRows();
  const {showQuotaTooltip, tagTooltipLabel, tagTooltipIsOpen} =
    tagButtonTooltipConfig({
      customer,
      isMyAccountsView,
      disabledActionTooltip,
      numSelected,
      activeAccounts: scoringRun.numActive,
      autoSelectTopRows,
    });

  const actionsEnabled =
    !disabledActionTooltip && !accountActions.isPending && numSelected;
  const actionsToShow = useActions();

  const tagsOnSelectedAccounts = numBulkSelected
    ? [] // If we're bulk selecting, we don't know what tags are on the selected accounts
    : _.uniqBy(
        fetchedAccounts
          .filter((account) => rowSelection[account.accountId.toString()])
          .flatMap((account) => account.mutatedState.tags ?? []),
        (tag) => tag.toString()
      );

  const allActionButtons = {
    exclude: (
      <ActionButtonWithTooltip
        key="exclude"
        isDisabled={!actionsEnabled}
        leftIcon={<TaskRemove />}
        onClick={() => updateAccounts({action: {name: 'exclude'}})}
        tooltipProps={{
          label: disabledActionTooltip,
        }}
        isCompact={isCompact}
      >
        Exclude
      </ActionButtonWithTooltip>
    ),
    reactivate: (
      <ActionButtonWithTooltip
        key="reactivate"
        isDisabled={!actionsEnabled}
        leftIcon={<Redo />}
        onClick={() => updateAccounts({action: {name: 'reactivate'}})}
        tooltipProps={{
          label: disabledActionTooltip,
        }}
        isCompact={isCompact}
      >
        Reactivate
      </ActionButtonWithTooltip>
    ),
    approve: (
      <ActionButtonWithTooltip
        key="save"
        isDisabled={!actionsEnabled}
        leftIcon={<CheckmarkOutline />}
        onClick={() => updateAccounts({action: {name: 'approve'}})}
        tooltipProps={{
          label: disabledActionTooltip,
        }}
        isCompact={isCompact}
      >
        Save
      </ActionButtonWithTooltip>
    ),
    disqualify: (
      <ActionButtonWithTooltip
        key="disqualify"
        isDisabled={!actionsEnabled}
        leftIcon={<Close />}
        onClick={() => updateAccounts({action: {name: 'disqualify'}})}
        tooltipProps={{
          label: disabledActionTooltip,
        }}
        isCompact={isCompact}
      >
        Disqualify
      </ActionButtonWithTooltip>
    ),
    override: (
      <TierOverridePopout
        key="override"
        onClick={(override) =>
          updateAccounts({action: {name: 'override', override}})
        }
        bulkNumberSelected={numSelected}
      >
        <ActionButtonWithTooltip
          isDisabled={!actionsEnabled}
          leftIcon={<ArrowsVertical />}
          tooltipProps={{
            label: disabledActionTooltip,
          }}
          isCompact={isCompact}
        >
          Override Tier
        </ActionButtonWithTooltip>
      </TierOverridePopout>
    ),
    tag: (
      <Fragment key="tag">
        <ActionButtonWithTooltip
          variant={isFreemiumDiscover ? 'glow' : 'ghost'}
          colorScheme={isFreemiumDiscover ? 'kbuttonblue' : undefined}
          isDisabled={!actionsEnabled || showQuotaTooltip}
          isLoading={accountActions.isPending}
          leftIcon={<TagEdit />}
          onClick={handleSaveAndTag}
          tooltipProps={{
            label: tagTooltipLabel,
            isOpen: tagTooltipIsOpen,
          }}
          isCompact={isCompact}
        >
          {isMyAccountsView
            ? 'Add to Lists'
            : isFreeOrListBuilderCustomer
              ? 'Save'
              : 'Save & Add to List'}
        </ActionButtonWithTooltip>
        <AccountTagsEditorModal
          bulkNumberSelected={numSelected}
          actionContext={actionContext}
          isOpen={tagAccountsModal.isOpen}
          onClose={tagAccountsModal.onClose}
          onTagsUpdated={() => {
            resetRowSelection();
          }}
          selectedTags={[]}
          tagOnly={isMyAccountsView}
        />

        {isMyAccountsView && (
          <>
            <ActionButtonWithTooltip
              isDisabled={
                !actionsEnabled ||
                (!numBulkSelected && tagsOnSelectedAccounts.length === 0)
              }
              isLoading={accountActions.isPending}
              leftIcon={<TagNone />}
              onClick={removeTagsModal.onOpen}
              tooltipProps={{
                label: tagTooltipLabel,
                isOpen: tagTooltipIsOpen,
              }}
              isCompact={isCompact}
            >
              Remove from Lists
            </ActionButtonWithTooltip>
            <AccountTagsEditorModal
              mode="remove"
              bulkNumberSelected={numSelected}
              actionContext={actionContext}
              isOpen={removeTagsModal.isOpen}
              onClose={removeTagsModal.onClose}
              onTagsUpdated={() => {
                resetRowSelection();
              }}
              selectedTags={[]}
              tagOnly
              filteredTagIds={tagsOnSelectedAccounts}
            />
          </>
        )}
      </Fragment>
    ),
  };

  const actionButtons = _.pick(allActionButtons, actionsToShow);
  return <>{Object.values(actionButtons)}</>;
};

type ActionButtonProps = ButtonProps & {
  tooltipProps: Omit<TooltipProps, 'children'>;
  isCompact?: boolean;
};

const ActionButtonWithTooltip = forwardRef<ActionButtonProps, 'button'>(
  ({children, tooltipProps, isCompact = true, ...buttonProps}, ref) => {
    return (
      <Tooltip
        hasArrow
        borderRadius="md"
        p="2"
        placement="top"
        {...tooltipProps}
        label={tooltipProps.label ?? (isCompact ? children : null)}
      >
        <Button
          color="kgray.400"
          fontSize="14"
          fontWeight="500"
          ref={ref}
          variant="ghost"
          flexShrink="0"
          iconSpacing={isCompact ? '0' : undefined}
          {...buttonProps}
        >
          {isCompact ? null : children}
        </Button>
      </Tooltip>
    );
  }
);

function tagButtonTooltipConfig({
  customer,
  isMyAccountsView,
  activeAccounts,
  numSelected,
  disabledActionTooltip,
  autoSelectTopRows,
}: {
  customer: MetadataResponse['customer'];
  isMyAccountsView: boolean;
  activeAccounts: number;
  numSelected: number;
  disabledActionTooltip: string | undefined;
  autoSelectTopRows: boolean;
}) {
  const isFreemiumCustomer = customer.plan.type === 'free';
  const isListBuilderCustomer = customer.plan.type === 'listBuilder';

  const activeAccountsLimit = getLimit(customer, 'activeAccounts');
  const {remaining: remainingSaves, limit: saveLimit} = getSavesQuota(customer);

  const showSaveQuotaTooltip =
    !isMyAccountsView && numSelected > remainingSaves;
  const showActiveAccountsQuotaTooltip =
    !isMyAccountsView && numSelected + activeAccounts > activeAccountsLimit;

  let tagTooltipLabel, tagTooltipIsOpen;
  if (disabledActionTooltip) {
    tagTooltipLabel = disabledActionTooltip;
  } else if (autoSelectTopRows) {
    tagTooltipLabel = 'Try saving your first 3 accounts!';
    tagTooltipIsOpen = true;
  } else if (showSaveQuotaTooltip) {
    const cta = isFreemiumCustomer
      ? `Upgrade to a paid plan to save more than ${saveLimit} accounts.`
      : isListBuilderCustomer
        ? 'Select fewer accounts or upgrade to a paid plan to save more.'
        : 'Please contact us for support.';
    tagTooltipLabel = (
      <Stack>
        <Box>
          Unable to save the selected number of accounts because it would exceed
          your remaining save credits.
        </Box>
        <Box>{cta}</Box>
      </Stack>
    );
  } else if (showActiveAccountsQuotaTooltip) {
    const cta =
      isFreemiumCustomer || isListBuilderCustomer
        ? `Select fewer accounts or upgrade to a paid plan to save more than ${activeAccountsLimit} total accounts.`
        : 'Please contact us for support.';
    tagTooltipLabel = (
      <Stack>
        <Box>
          Unable to save the selected number of accounts because it would exceed
          your account limit.
        </Box>
        <Box>{cta}</Box>
      </Stack>
    );
  }

  return {
    showQuotaTooltip: showSaveQuotaTooltip || showActiveAccountsQuotaTooltip,
    tagTooltipLabel,
    tagTooltipIsOpen,
  };
}
